import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cashExchangeService } from "../api/services/cashExchange/cashExchangeService";
import toast from "react-hot-toast";
import { useState, useCallback } from "react";
import {
  CashInRequest,
  CashInRequestByPlayerAndAgentId,
  CashOutRequest,
} from "../api/services/cashExchange/type";

const CASH_EXCHANGE_STALE_TIME =
  Number(process.env.REACT_APP_CASH_EXCHANGE_STALE_TIME) || 5 * 60 * 1000;

export const cashExchangeKeys = {
  all: ["cashExchange"] as const,
  cashIns: () => [...cashExchangeKeys.all, "cashIn"] as const,
  cashOuts: () => [...cashExchangeKeys.all, "cashOut"] as const,
  cashRecords: () => [...cashExchangeKeys.all, "cashRecords"] as const,
  customerCashRecords: (customerId: string) =>
    [...cashExchangeKeys.cashRecords(), "customer", customerId] as const,
  playerCashIns: (playerId: string) =>
    [...cashExchangeKeys.cashIns(), "player", playerId] as const,
  playerCashOuts: (playerId: string) =>
    [...cashExchangeKeys.cashOuts(), "player", playerId] as const,
  customerCashIns: (customerId: string) =>
    [...cashExchangeKeys.cashIns(), "customer", customerId] as const,
  customerCashOuts: (customerId: string) =>
    [...cashExchangeKeys.cashOuts(), "customer", customerId] as const,
  allCashIns: () => [...cashExchangeKeys.cashIns(), "all"] as const,
  allCashOuts: () => [...cashExchangeKeys.cashOuts(), "all"] as const,
};

export const useCashInByPlayerAndAgentId = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CashInRequestByPlayerAndAgentId) =>
      cashExchangeService.createCashInByPlayerAndAgentId(data),
    onSuccess: (response) => {
      toast.success(
        response.message || "Cash In transaction completed successfully!",
      );
      queryClient.invalidateQueries({
        queryKey: ["cashIns", response.data.customerId],
      });
      queryClient.invalidateQueries({
        queryKey: cashExchangeKeys.cashRecords(),
      });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to process cash in transaction";
      toast.error(errorMessage);
    },
  });
};

export const useCashInByPlayerId = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CashInRequest) =>
      cashExchangeService.createCashInByPlayerId(data),
    onSuccess: (response) => {
      toast.success(
        response.message || "Cash In transaction completed successfully!",
      );
      queryClient.invalidateQueries({
        queryKey: ["cashIns", response.data.customerId],
      });
      queryClient.invalidateQueries({
        queryKey: cashExchangeKeys.cashRecords(),
      });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to process cash in transaction";
      toast.error(errorMessage);
    },
  });
};

export const useCashInsByPlayer = (playerId: string, enabled = true) => {
  return useQuery({
    queryKey: ["cashIns", playerId],
    queryFn: () => cashExchangeService.getCashInsByPlayerId(playerId),
    enabled: enabled && !!playerId,
    staleTime: CASH_EXCHANGE_STALE_TIME,
  });
};

export const useCustomerCashRecords = (
  customerId: string,
  params?: {
    skip?: number;
    limit?: number;
    date?: string;
    fromDate?: string;
    toDate?: string;
    shift?: string;
    recordType?: "cash_in" | "cash_out" | "all";
    paymentType?: string;
    export?: boolean;
  },
) => {
  return useQuery({
    queryKey: [...cashExchangeKeys.customerCashRecords(customerId), params],
    queryFn: () =>
      cashExchangeService.getCustomerCashRecords(customerId, params),
    enabled: !!customerId,
    staleTime: CASH_EXCHANGE_STALE_TIME,
  });
};

export const useCashRecords = (params?: {
  skip?: number;
  limit?: number;
  date?: string;
  fromDate?: string;
  toDate?: string;
  shift?: string;
  customerId?: string;
  recordType?: "cash_in" | "cash_out" | "all";
  paymentType?: string;
  export?: boolean;
}) => {
  return useQuery({
    queryKey: [...cashExchangeKeys.cashRecords(), params],
    queryFn: () => cashExchangeService.getCashRecords(params),
    staleTime: CASH_EXCHANGE_STALE_TIME,
  });
};

export const useCashRecordsForExport = (params?: {
  date?: string;
  fromDate?: string;
  toDate?: string;
  shift?: string;
  customerId?: string;
  recordType?: "cash_in" | "cash_out" | "all";
  paymentType?: string;
}) => {
  const exportParams = { ...params, export: true };

  return useQuery({
    queryKey: [...cashExchangeKeys.cashRecords(), exportParams, "export"],
    queryFn: () => cashExchangeService.getCashRecords(exportParams),
    enabled: false,
    staleTime: 0,
  });
};

export const useCustomerCashIns = (
  customerId: string,
  params?: {
    date?: string;
    fromDate?: string;
    toDate?: string;
    shift?: string;
  },
) => {
  return useQuery({
    queryKey: [
      ...cashExchangeKeys.customerCashIns(customerId),
      params?.date,
      params?.fromDate,
      params?.toDate,
      params?.shift,
    ],
    queryFn: () =>
      cashExchangeService.getCashInsByCustomerId(customerId, {
        limit: 25,
        ...params,
      }),
    enabled: !!customerId,
    staleTime: CASH_EXCHANGE_STALE_TIME,
  });
};

export const useCashIns = (params?: {
  date?: string;
  fromDate?: string;
  toDate?: string;
  shift?: string;
  customerId?: string;
  paymentType?: string;
}) => {
  return useQuery({
    queryKey: [...cashExchangeKeys.allCashIns(), params],
    queryFn: () => cashExchangeService.getCashIns({ limit: 25, ...params }),
    enabled: true,
    staleTime: CASH_EXCHANGE_STALE_TIME,
  });
};

export const useCashOut = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CashOutRequest) =>
      cashExchangeService.createCashOut(data),
    onSuccess: (response) => {
      toast.success(
        response.message || "Cash Out transaction completed successfully!",
      );

      if (response.data.length > 0) {
        queryClient.invalidateQueries({
          queryKey: ["cashOuts", response.data[0].customerId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: cashExchangeKeys.cashRecords(),
      });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to process cash out transaction";
      toast.error(errorMessage);
    },
  });
};

export const useCashOutsByPlayer = (playerId: string, enabled = true) => {
  return useQuery({
    queryKey: ["cashOuts", playerId],
    queryFn: () => cashExchangeService.getCashOutsByPlayerId(playerId),
    enabled: enabled && !!playerId,
    staleTime: CASH_EXCHANGE_STALE_TIME,
  });
};

export const useCustomerCashOuts = (
  customerId: string,
  params?: {
    date?: string;
    fromDate?: string;
    toDate?: string;
    shift?: string;
  },
) => {
  return useQuery({
    queryKey: [
      ...cashExchangeKeys.customerCashOuts(customerId),
      params?.date,
      params?.fromDate,
      params?.toDate,
      params?.shift,
    ],
    queryFn: () =>
      cashExchangeService.getCashOutsByCustomerId(customerId, {
        limit: 25,
        ...params,
      }),
    enabled: !!customerId,
    staleTime: CASH_EXCHANGE_STALE_TIME,
  });
};

export const useCashOuts = (params?: {
  date?: string;
  fromDate?: string;
  toDate?: string;
  shift?: string;
  customerId?: string;
}) => {
  return useQuery({
    queryKey: [...cashExchangeKeys.allCashOuts(), params],
    queryFn: () => cashExchangeService.getCashOuts({ limit: 25, ...params }),
    enabled: true,
    staleTime: CASH_EXCHANGE_STALE_TIME,
  });
};

export const useCashRecordManagement = (
  initialParams: {
    skip?: number;
    limit?: number;
    date?: string;
    fromDate?: string;
    toDate?: string;
    shift?: string;
    customerId?: string;
    recordType?: "cash_in" | "cash_out" | "all";
    paymentType?: string;
  } = {},
) => {
  const [error, setError] = useState<Error | null>(null);

  const {
    data: cashRecordsData,
    isLoading,
    isError,
    error: queryError,
    refetch,
  } = useCashRecords(initialParams);

  const {
    isLoading: isExporting,
    error: exportError,
    refetch: refetchExport,
  } = useCashRecordsForExport(initialParams);

  const refreshCashRecords = useCallback(() => {
    refetch();
  }, [refetch]);

  const exportCashRecords = useCallback(async () => {
    try {
      const result = await refetchExport();
      return result.data;
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, [refetchExport]);

  return {
    cashRecords: cashRecordsData?.items || [],
    pagination: cashRecordsData?.pagination || null,
    filtersApplied: cashRecordsData?.filtersApplied || null,
    isLoading,
    isError,
    error: queryError || error,
    refreshCashRecords,

    exportCashRecords,
    isExporting,
    exportError,
  };
};

export const useCashExchangeHistory = (playerId: string, enabled = true) => {
  const cashIns = useCashInsByPlayer(playerId, enabled);
  const cashOuts = useCashOutsByPlayer(playerId, enabled);

  return {
    cashIns,
    cashOuts,
    isLoading: cashIns.isLoading || cashOuts.isLoading,
    isError: cashIns.isError || cashOuts.isError,
    error: cashIns.error || cashOuts.error,
  };
};
